import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Alert from '@mui/joy/Alert';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import UserContext from '../context/UserContext';
import axiosConfig from '../util/axiosConfig';
import { clearStoredAuth, getStoredToken, setStoredToken } from '../util/authStorage';
import {
    syncPushTokenIfGranted,
    unregisterPushTokenBeforeLogout
} from '../util/pushNotifications';
import Link from '@mui/joy/Link';
import { usePageTitle } from '../util/usePageTitle';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    usePageTitle('Login');
    const [studentId, setStudentId] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { setUser } = useContext(UserContext);

    useEffect(() => {
        if (getStoredToken()) {
            const redirect = new URLSearchParams(location.search).get('redirect');
            navigate(redirect || '/');
        }
    }, [navigate, location.search]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        const form = e.currentTarget;
        const formData = new FormData(form);
        const submittedStudentId = (
            formData.get('studentId') ?? studentId ?? ''
        ).toString().trim();
        const submittedPassword = (
            formData.get('password') ?? password ?? ''
        ).toString();

        if (!submittedStudentId || !submittedPassword) {
            setError('Please enter Student ID and password.');
            setIsSubmitting(false);
            return;
        }

        try {
            if (getStoredToken()) {
                await unregisterPushTokenBeforeLogout();
            }
            clearStoredAuth();

            const response = await axiosConfig.post(`/auth/login`, {
                studentId: submittedStudentId,
                password: submittedPassword
            }, {
                withCredentials: true
            });

            if (response.data.success) {
                // Store token in localStorage for axios interceptor
                setStoredToken(response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                // Set user directly in context to avoid extra API call
                setUser(response.data.user);
                syncPushTokenIfGranted({ preferServer: true }).catch((err) => {
                    console.warn('Push token sync after login failed:', err);
                });
                const redirect = new URLSearchParams(location.search).get('redirect');
                navigate(redirect || '/');
            }
        } catch (error) {
            if (error.response?.status === 401) {
                setError(error.response?.data?.errorMessage || 'Invalid student ID or password');
            } else {
                setError(error.response?.data?.errorMessage || 'Login failed');
            }
            // Clear any partial data on error
            clearStoredAuth();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'auto',
                minHeight: 'auto',
                alignItems: 'center',
                justifyContent: 'center',
                py: '20px',
                p: 2
            }}
        >
            <Sheet
                variant="outlined"
                sx={{
                    width: '100%',
                    maxWidth: 400,
                    mx: 'auto',
                    p: 3,
                    borderRadius: 'md',
                    boxShadow: 'md'
                }}
            >
                <form onSubmit={handleSubmit} method="post">
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <Logo clickable={false} width={250} height={83} />
                    </Box>

                    <Typography
                        level="body-sm"
                        sx={{
                            textAlign: 'center',
                            mb: 2
                        }}
                    >
                        Sign in to continue
                    </Typography>

                    {error && (
                        <Alert
                            variant="soft"
                            color="danger"
                            sx={{ mb: 2 }}
                            onClose={() => setError('')}
                        >
                            {error}
                        </Alert>
                    )}

                    <FormControl sx={{ mb: 2 }}>
                        <FormLabel>Student ID </FormLabel>
                        <Input
                            type="text"
                            name="studentId"
                            autoComplete="username"
                            placeholder="Student ID"
                            value={studentId}
                            onChange={(e) => setStudentId(e.target.value)}
                            onInput={(e) => setStudentId(e.target.value)}
                            required
                            disabled={isSubmitting}
                        />
                    </FormControl>

                    <FormControl sx={{ mb: 2 }}>
                        <FormLabel>Password</FormLabel>
                        <Input
                            type="password"
                            name="current-password"
                            autoComplete="current-password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onInput={(e) => setPassword(e.target.value)}
                            required
                            disabled={isSubmitting}
                        />
                        <Box sx={{ mt: 1, textAlign: 'right' }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => navigate('/forgot-password')}
                                sx={{
                                    color: '#ea7a23',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Forgot Password?
                            </Link>
                        </Box>
                    </FormControl>

                    <Button
                        type="submit"
                        fullWidth
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        sx={{
                            mt: 1,
                            bgcolor: '#ea7a23',
                            '&:hover': {
                                bgcolor: '#d06a1e',
                            }
                        }}
                    >
                        {isSubmitting ? 'Signing in...' : 'Sign in'}
                    </Button>

                    {/* <Divider sx={{ my: 2 }}>or</Divider>

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography level="body-sm">
                            Don't have an account?{' '}
                            <Link
                                href="/register"
                                sx={{
                                    color: '#ea7a23',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Sign up
                            </Link>
                        </Typography>
                    </Box> */}
                </form>
            </Sheet>
        </Box>
    );
};

export default Login; 